// QaReport — output of the QA fact-check pass (Session 19AA · §12).
//
// AI_INGEST_STRATEGY.md §12 论证：
//   After 4 families finish, run one extra LLM call (V4-Pro thinking) over
//   the entire IngestPackage to audit fact consistency, quantity coverage,
//   Bloom distribution, duplicates, cross-references, and topic gaps.
//
//   Output is a structured report; orchestrator may auto-apply *safe additive*
//   patches (added flashcards / glossary, fixed explanations) but never apply
//   structural changes (modifying mcq.correct, deleting items) without
//   陛下 review.
//
//   QA failure modes are non-fatal — IngestPackage stays usable; QA badge
//   surfaces severity to dashboard.
import { z } from 'zod';
import { FlashcardSchema } from './flashcard.js';
import { GlossaryItemSchema } from './glossary.js';
export const QaIssueSeveritySchema = z.enum(['error', 'warn', 'info']);
export const QaIssueKindSchema = z.enum([
    'fact_consistency', // numbers/dates/entities cross-component disagree
    'quantity_audit', // chapter under perChapterTarget min
    'bloom_distribution', // cog level skewed (all Apply, no Recall)
    'duplicate_detection', // option-level / glossary-level duplicates
    'cross_reference_integrity', // unresolved caseId / topicId
    'coverage_gap', // topic node has 0 component touches
]);
export const QaIssueSchema = z.object({
    severity: QaIssueSeveritySchema,
    kind: QaIssueKindSchema,
    /** Path into IngestPackage, e.g. 'mcqs[3].explanation'. */
    location: z.string(),
    message: z.string(),
    /** Suggested patch (free-form per kind); orchestrator decides applicability. */
    suggestedPatch: z.unknown().optional(),
});
/** Patches the orchestrator may auto-apply WITHOUT 陛下 review.
 *  Strictly additive / field-level — never structural. */
export const QaSafePatchesSchema = z.object({
    /** Added flashcards (covers missing-quantity gaps). */
    addedFlashcards: z.array(FlashcardSchema).optional(),
    /** Added glossary terms. */
    addedGlossary: z.array(GlossaryItemSchema).optional(),
    /** Map: mcq.id → corrected explanation text (typo / number fix only). */
    fixedMcqExplanations: z.record(z.string()).optional(),
});
export const QaReportSchema = z.object({
    generatedAt: z.string(),
    /** Which model produced this report (e.g. 'deepseek-v4-pro@thinking'). */
    modelVersion: z.string(),
    /** Overall pass — true if 0 errors. */
    passed: z.boolean(),
    issueCount: z.object({
        error: z.number().int().min(0),
        warn: z.number().int().min(0),
        info: z.number().int().min(0),
    }),
    issues: z.array(QaIssueSchema),
    /** Auto-applicable patches; orchestrator merges into IngestPackage if non-empty. */
    patches: QaSafePatchesSchema.optional(),
    /** Token usage for cost tracking (Phase 4 monitoring · §10). */
    tokenUsage: z.object({
        inputTokens: z.number().int().min(0),
        outputTokens: z.number().int().min(0),
    }).optional(),
}).passthrough();
/** Empty placeholder — used when QA call failed and we want a non-blocking
 *  default. Dashboard renders this as "QA unavailable" status. */
export function emptyQaReport(modelVersion) {
    return {
        generatedAt: new Date().toISOString(),
        modelVersion,
        passed: false,
        issueCount: { error: 0, warn: 0, info: 0 },
        issues: [],
    };
}
/** Severity threshold for dashboard QA badge color.
 *  Returns 'green' | 'yellow' | 'red' per §12.3 spec. */
export function qaBadgeColor(report) {
    const errors = report.issueCount.error;
    if (errors === 0)
        return 'green';
    if (errors <= 3)
        return 'yellow';
    return 'red';
}
//# sourceMappingURL=qa-report.js.map